import Alert from "@components/alert";
import BusyIndicator from "@components/busyIndicator";
import { IChatMessage } from "@components/chatMessage/ChatMessage.utils";
import { FlagDotFilledIcon, FlagPlainIcon } from "@components/icon";
import { IEntity } from "@odata/BindingContext";
import { ChatEntity, IChatEntity, IChatMessageEntity } from "@odata/GeneratedEntityTypes";
import { BatchRequest } from "@odata/OData";
import { IconButtonStyled } from "@pages/chats/chatUserSelect.styles";
import HandoverDialog from "@pages/chats/HandoverDialog";
import customFetch, { getDefaultPostParams } from "@utils/customFetch";
import { ChatOperationType, ContextEvents } from "contexts/appContext/AppContext.types";
import i18next from "i18next";
import { cloneDeep } from "lodash";
import React from "react";

import { BreadCrumbProvider } from "../../components/breadCrumb";
import { TOGGLE_CHATS_FLAGS } from "../../constants";
import { WithAuthContext, withAuthContext } from "../../contexts/authContext/withAuthContext";
import { withPermissionContext } from "../../contexts/permissionContext/withPermissionContext";
import { IconSize, Status, ToolbarItemType } from "../../enums";
import { getLocalTimeDate } from "../../types/Date";
import FileStorage from "../../utils/FileStorage";
import { getAlertFromError } from "../../views/formView/Form.utils";
import { FormViewForExtend, IFormViewProps } from "../../views/formView/FormView";
import { IChatCustomData } from "./Chats.def";
import { ChatAlertArea, ChatBcWrapper, ChatsFormViewStyled, SmartHeaderTicketsStyled } from "./Chats.styles";
import {
    canTakeOverChat,
    getChatMessageFromCreatedMessage,
    getLinkPropertyByEntityType,
    getMyPropName,
    handover,
    hasUnreadMessage,
    isMessageMine,
    myUserProp
} from "./Chats.utils";
import SmartChatMessageStream from "./SmartChatMessageStream";
import TestIds from "../../testIds";


interface IProps extends WithAuthContext, IFormViewProps<IChatEntity, IChatCustomData> {
}

class ChatsFormView extends FormViewForExtend<IChatEntity, IProps> {
    static defaultProps = {
        formProps: {
            shouldHideAuditTrail: true,
            shouldHideVariant: true
        }
    };

    _showHandoverDialog = false;

    get isInDialog() {
        return this.props.storage.getCustomData().isInDialog;
    }

    get Recipient() {
        return ChatEntity.Recipient;
    }

    get Sender() {
        return ChatEntity.Sender;
    }

    get Me() {
        return this.props.storage.context.getData().userSettings;
    }

    get ShouldUseCustomMessages() {
        return this.props.storage.getCustomData().useCustomMessages;
    }

    get isFlagged() {
        const { storage } = this.props;
        const entity = storage.data.entity;
        const prop = myUserProp(storage.context, entity);

        if (prop) {
            return entity[prop]?.IsFlagged;
        }

        return false;
    }

    get isMine() {
        const { storage } = this.props;
        return this.isNew || isMessageMine(this.Me, storage.data.entity);
    }

    get isNew() {
        return this.props.storage.data.bindingContext.isNew();
    }

    async onAfterLoad(): Promise<void> {
        const { storage } = this.props;

        const entity = this.props.storage.data.entity;
        const hasUnreadMessages = hasUnreadMessage(this.props.storage, entity);
        if (hasUnreadMessages) {
            const myPropName = getMyPropName(storage.context, entity);
            if (myPropName) {
                const data = {
                    [myPropName]: {
                        DateLastRead: getLocalTimeDate(),
                    }
                };

                const batch: BatchRequest = storage.oData.batch();
                const queryableEntity = batch.fromPath(storage.data.bindingContext.toString());
                queryableEntity.update(null, data);
                batch.execute().then(() => {
                    storage.context.eventEmitter.emit(ContextEvents.ChatOperation, {
                        chatBc: storage.data.bindingContext,
                        type: ChatOperationType.Read
                    });
                });
            }
        }

        const chatData = (storage.getCustomData() as IChatCustomData).chatData;

        if (chatData) {
            storage.data.entity.Messages = chatData.messages as IChatMessageEntity[];
            storage.setValueByPath(ChatEntity.Title, chatData.title);
        }

        return super.onAfterLoad();
    }

    onBeforeSave(data?: IChatEntity): IChatEntity {
        const { storage } = this.props;
        const entity = cloneDeep(data ?? storage.data.entity) as IEntity;
        if (storage.data.bindingContext.isNew()) {
            entity[this.Sender] = {
                User: {
                    Id: this.props.authContext.userId
                }
            };

            entity[this.Recipient] = {
                User: {
                    Id: (storage.getCustomData() as IChatCustomData).user?.id
                }
            };

            const isNew = storage.data.bindingContext.isNew();
            const cd = storage.getCustomData();
            const bc = cd.documentBindingContext;
            if (bc) {
                const isDraft = cd.isDraft;
                const propName = getLinkPropertyByEntityType(cd.entityType, isDraft);

                entity[propName] = {
                    Id: cd.id,
                    _metadata: isNew && !isDraft ? {
                        "": { type: `#${bc.getEntityType().getFullName()}` }
                    } : undefined
                };
            }
        }

        return entity;
    }

    onAfterSave(isNew: boolean, preventTableViewRefresh: boolean): void {
        this.props.onAfterSave?.(isNew, true);
    }


    handleCreateMessage = async (message: IChatMessage, files: File[]): Promise<boolean> => {
        const { storage } = this.props;
        const { entity } = storage.data;
        this.props.storage.setBusy(true);

        if (message.subject && !entity.Messages?.length) {
            storage.setValueByPath(ChatEntity.Title, message.subject);
        }

        let enhancedMessage: IChatMessageEntity;
        const currentMessages = storage.getValueByPath(ChatEntity.Messages) ?? [];
        try {
            const filePromises = files.map(file => FileStorage.upload({ file }));
            const fileMetadataEntities = await Promise.all(filePromises);

            enhancedMessage = getChatMessageFromCreatedMessage(storage, message, fileMetadataEntities);
        } catch (error) {
            storage.setFormAlert(getAlertFromError(error));
        }

        const messages = [...currentMessages, enhancedMessage];
        storage.setValueByPath(ChatEntity.Messages, messages);

        let isOk = true;
        if (!this.ShouldUseCustomMessages) {
            const result = await this.save({
                successSubtitle: null,
                // just adding a new message, we don't need to check for data consistency
                skipETag: true,
                skipLoad: true
            });

            if (result) {
                // no await, it makes better "flow" of the control
                storage.loadDataAfterSave();
            }

            isOk = !!result;
        }

        const cd = storage.getCustomData();

        storage.context.eventEmitter.emit(ContextEvents.ChatOperation, {
            id: cd.id,
            data: {
                // todo: switch user ID to from id in whole custom date env.
                user: {
                    ...cd.user,
                    Id: cd.user?.id
                },
                messages,
                title: entity[ChatEntity.Title]
            },
            documentBc: cd.documentBindingContext,
            type: ChatOperationType.Create
        });

        this.props.storage.setBusy(false);

        return isOk;
    };

    handleFlagClick = async () => {
        const { storage } = this.props;
        const body = {
            ChatIds: [storage.data.bindingContext.getKey()]
        };

        storage.setBusy(true);

        const response = await customFetch(TOGGLE_CHATS_FLAGS, {
            ...getDefaultPostParams(),
            body: JSON.stringify(body)
        });

        this.props.onAfterSave?.(false);
        await storage.reload();

        storage.setBusy(false);
    }

    renderFlagIcon = () => {
        const Icon = this.isFlagged ? FlagDotFilledIcon : FlagPlainIcon;
        const { storage } = this.props;

        const isMine = isMessageMine(storage.context.getData().userSettings, storage.data.entity);

        if (this.props.storage.data.bindingContext.isNew() || !isMine) {
            return null;
        }

        return (
            <IconButtonStyled key={"Flag"} title={i18next.t("Chat:Flag")}
                              onClick={this.handleFlagClick}
                              isDisabled={this.props.storage.isDisabled || this.props.storage.isReadOnly}
                              isDecorative>
                <Icon width={IconSize.XS}/>
            </IconButtonStyled>
        );
    }

    handleHandoverClick = async () => {
        if (this.isMine) {
            this._showHandoverDialog = true;
            this.forceUpdate();
        } else {
            const error = await handover([this.props.storage.data.entity.Id?.toString()], this.Me?.Id.toString());

            if (!error) {
                this.handleHandoverFinish(true);
            } else {
                this.props.storage.setFormAlert(error);
            }
        }
    }

    handleHandoverFinish = async (reload: boolean) => {
        this._showHandoverDialog = false;

        if (reload) {
            await this.props.storage.loadDataAfterSave();
            this.props.onAfterSave?.(true);
        }
        this.forceUpdate();
    }

    renderHandoverDialog = () => {
        const storage = this.props.storage;
        if (this._showHandoverDialog) {
            const me = storage.context.getData().userSettings;
            const isMine = isMessageMine(me, storage.data.entity);

            return (
                <HandoverDialog skippedUserId={isMine ? me?.Id?.toString() : null}
                                chatIds={[this.props.storage.data.entity.Id?.toString()]}
                                onClose={this.handleHandoverFinish}/>
            );
        }

        return null;
    }

    getHeaderIcons = () => {
        const icons = super.getHeaderIcons();
        const storage = this.props.storage;

        if (!this.isNew && (canTakeOverChat(storage.data.entity, storage.context))) {
            icons.push({
                id: `Handover`,
                label: this.isMine ? i18next.t("Chat:Handover") : i18next.t("Chat:Takeover"),
                itemType: ToolbarItemType.Icon,
                iconName: "ChatHandOver",
                onClick: this.handleHandoverClick
            });
        }

        return icons;
    }

    renderForm(): React.ReactElement {
        const alert = this.props.storage.data?.alert;
        return (
            <ChatsFormViewStyled data-testid={TestIds.ChatDetail}>
                {this.props.storage.isBusy() && this.props.storage.afterLoaded && <BusyIndicator/>}
                {this.renderHandoverDialog()}

                {this.shouldShowBreadCrumbs &&
                    <ChatBcWrapper>
                        <BreadCrumbProvider
                            back={false}
                            customIcon={this.renderFlagIcon()}
                        />
                    </ChatBcWrapper>
                }
                {this.props.storage.isBusy() && <BusyIndicator/>}
                <SmartHeaderTicketsStyled
                    ref={this._refHeader}
                    title={this.getFormTitle()}
                    subtitle={this.getFormSubtitle()}
                    getCustomHeaderInfo={() => {
                        // BE sends 'read only' info but we don't want to display it
                        return { infoText: null };
                    }}
                    subtitleStatus={this.getFormSubtitleStatus()}
                    hotspotId={"formHeader"}
                    storage={this.props.storage}
                    icons={this.getHeaderIcons()}
                    shouldHideVariant={true}
                    onChange={this.handleChange}/>

                {alert &&
                    <ChatAlertArea>
                        <Alert
                            isFullWidth
                            status={Status.Error}
                            title={alert.title}
                            detailData={alert.detailData}
                            subTitle={alert.subTitle}
                        />
                    </ChatAlertArea>
                }

                <SmartChatMessageStream storage={this.props.storage}
                                        key={this.props.storage.data?.entity?.Id}
                                        isReadOnly={!this.isMine || this.props.storage.isDisabled || this.props.storage.isReadOnly}
                                        onCreateMessage={this.handleCreateMessage}/>
            </ChatsFormViewStyled>
        );
    }
}

export default withPermissionContext(withAuthContext(ChatsFormView));
